export type TourViewManifest = { settled: boolean; anchors: ReadonlySet<string> };
export type AnchorPresence = "visible-later" | "absent" | "pending" | "unknown";

const views = new Map<string, TourViewManifest>();
const listeners = new Set<() => void>();

const sameManifest = (a: TourViewManifest, b: TourViewManifest): boolean => {
  if (a.settled !== b.settled || a.anchors.size !== b.anchors.size) {
    return false;
  }
  for (const anchor of a.anchors) {
    if (!b.anchors.has(anchor)) {
      return false;
    }
  }
  return true;
};

const notify = (): void => {
  for (const listener of Array.from(listeners)) {
    listener();
  }
};

export const publishTourView = (
  viewId: string,
  manifest: { settled: boolean; anchors: Iterable<string> },
): void => {
  const next: TourViewManifest = { settled: manifest.settled, anchors: new Set(manifest.anchors) };
  const previous = views.get(viewId);
  if (previous && sameManifest(previous, next)) {
    return;
  }
  views.set(viewId, next);
  notify();
};

export const retractTourView = (viewId: string): void => {
  if (!views.delete(viewId)) {
    return;
  }
  notify();
};

export const subscribeTourViews = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const resolveAnchorPresence = (anchorId: string): AnchorPresence => {
  if (views.size === 0) {
    return "unknown";
  }
  let pending = false;
  for (const manifest of views.values()) {
    if (manifest.anchors.has(anchorId)) {
      return "visible-later";
    }
    if (!manifest.settled) {
      pending = true;
    }
  }
  return pending ? "pending" : "absent";
};

export const resetTourViewsForTests = (): void => {
  views.clear();
  listeners.clear();
};
